import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft, TrendingUp, TrendingDown, PoundSterling, Home,
  AlertTriangle, CheckCircle, Users, Calendar, Loader2,
  BarChart3, PieChart, Activity, Shield, Clock
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell, LineChart, Line, Area, AreaChart
} from "recharts";
import { API } from "@/App";

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export const LettingsAnalyticsPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/lettings/analytics`, { headers });
      setAnalytics(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const rentData = analytics?.rent_trends || [];
  const complianceData = analytics?.compliance_summary || [];
  const propertyStats = analytics?.property_stats || {};
  const arrearsData = analytics?.arrears_trends || [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-700 to-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate(-1)}
              className="text-white hover:bg-blue-600"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Lettings Analytics</h1>
              <p className="text-blue-200">Performance metrics & trends</p>
            </div>
          </div>
        </div>
      </header>

      {/* KPI Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-white shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Properties</p>
                  <p className="text-2xl font-bold">{propertyStats.total_properties || 0}</p>
                </div>
                <Home className="w-8 h-8 text-blue-600" />
              </div>
              <div className="mt-2 flex items-center text-sm">
                <Badge variant="secondary">{propertyStats.occupied || 0} occupied</Badge>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Rent Collection</p>
                  <p className="text-2xl font-bold">{analytics?.collection_rate || 0}%</p>
                </div>
                <PoundSterling className="w-8 h-8 text-emerald-600" />
              </div>
              <div className="mt-2 flex items-center text-sm">
                {analytics?.collection_rate >= 95 ? (
                  <span className="text-emerald-600 flex items-center">
                    <TrendingUp className="w-4 h-4 mr-1" /> On target
                  </span>
                ) : (
                  <span className="text-amber-600 flex items-center">
                    <TrendingDown className="w-4 h-4 mr-1" /> Below target
                  </span>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Arrears</p>
                  <p className="text-2xl font-bold">£{(analytics?.total_arrears || 0).toLocaleString()}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <div className="mt-2 flex items-center text-sm">
                <Badge variant="destructive">{analytics?.tenants_in_arrears || 0} tenants</Badge>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Compliance Alerts</p>
                  <p className="text-2xl font-bold">{analytics?.compliance_alerts || 0}</p>
                </div>
                <Shield className="w-8 h-8 text-amber-600" />
              </div>
              <div className="mt-2 flex items-center text-sm">
                {analytics?.overdue_compliance > 0 ? (
                  <Badge variant="destructive">{analytics?.overdue_compliance} overdue</Badge>
                ) : (
                  <Badge variant="secondary">All current</Badge>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="rent">Rent & Arrears</TabsTrigger>
            <TabsTrigger value="compliance">Compliance</TabsTrigger>
            <TabsTrigger value="properties">Properties</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Rent Collection Trend */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-blue-600" />
                    Rent Collection Trend
                  </CardTitle>
                  <CardDescription>Monthly rent collection rate</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={rentData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis domain={[0, 100]} />
                      <Tooltip formatter={(value) => `${value}%`} />
                      <Area 
                        type="monotone" 
                        dataKey="collection_rate" 
                        stroke="#3b82f6" 
                        fill="#93c5fd"
                        name="Collection Rate"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Compliance Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-emerald-600" />
                    Compliance Status
                  </CardTitle>
                  <CardDescription>Certificate status breakdown</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <RechartsPie>
                      <Pie
                        data={complianceData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {complianceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </RechartsPie>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Arrears Trend */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingDown className="w-5 h-5 text-red-600" />
                    Arrears Trend
                  </CardTitle>
                  <CardDescription>Monthly arrears balance</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={arrearsData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip formatter={(value) => `£${value.toLocaleString()}`} />
                      <Line 
                        type="monotone" 
                        dataKey="arrears" 
                        stroke="#ef4444" 
                        strokeWidth={2}
                        dot={{ fill: '#ef4444' }}
                        name="Arrears"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Property Types */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-purple-600" />
                    Property Portfolio
                  </CardTitle>
                  <CardDescription>By property type</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={analytics?.property_types || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="type" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#8b5cf6" name="Properties" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Rent & Arrears Tab */}
          <TabsContent value="rent">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>Monthly Rent Summary</CardTitle>
                  <CardDescription>Rent expected vs collected</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={rentData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip formatter={(value) => `£${value.toLocaleString()}`} />
                      <Legend />
                      <Bar dataKey="expected" fill="#94a3b8" name="Expected" />
                      <Bar dataKey="collected" fill="#10b981" name="Collected" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Top Arrears */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-red-700">Top Arrears Cases</CardTitle>
                  <CardDescription>Tenants with highest arrears</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(analytics?.top_arrears || []).map((tenant, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <div>
                          <p className="font-medium">{tenant.name}</p>
                          <p className="text-sm text-muted-foreground">{tenant.property}</p>
                        </div>
                        <Badge variant="destructive">£{tenant.arrears?.toLocaleString()}</Badge>
                      </div>
                    ))}
                    {(!analytics?.top_arrears || analytics.top_arrears.length === 0) && (
                      <p className="text-center text-muted-foreground py-8">No arrears data</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Collection by Landlord */}
              <Card>
                <CardHeader>
                  <CardTitle>Collection by Landlord</CardTitle>
                  <CardDescription>Portfolio performance</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(analytics?.landlord_collection || []).slice(0, 5).map((ll, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{ll.name}</p>
                          <p className="text-sm text-muted-foreground">{ll.properties} properties</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-emerald-600">{ll.collection_rate}%</p>
                          <p className="text-xs text-muted-foreground">£{ll.collected?.toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Compliance Tab */}
          <TabsContent value="compliance">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Gas Safety */}
              <Card>
                <CardHeader className="bg-orange-50">
                  <CardTitle className="flex items-center gap-2 text-orange-700">
                    <Shield className="w-5 h-5" />
                    Gas Safety
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Valid</span>
                      <Badge variant="secondary">{analytics?.gas_valid || 0}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Due in 30 days</span>
                      <Badge className="bg-amber-100 text-amber-700">{analytics?.gas_due_soon || 0}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Overdue</span>
                      <Badge variant="destructive">{analytics?.gas_overdue || 0}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Exempt</span>
                      <Badge variant="outline">{analytics?.gas_exempt || 0}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* EPC */}
              <Card>
                <CardHeader className="bg-green-50">
                  <CardTitle className="flex items-center gap-2 text-green-700">
                    <Activity className="w-5 h-5" />
                    EPC Certificates
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Valid</span>
                      <Badge variant="secondary">{analytics?.epc_valid || 0}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Due in 30 days</span>
                      <Badge className="bg-amber-100 text-amber-700">{analytics?.epc_due_soon || 0}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Expired</span>
                      <Badge variant="destructive">{analytics?.epc_overdue || 0}</Badge>
                    </div>
                  </div>
                  
                  {/* EPC Rating Distribution */}
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm font-medium mb-2">Rating Distribution</p>
                    <div className="flex gap-1">
                      {['A', 'B', 'C', 'D', 'E', 'F', 'G'].map((rating) => (
                        <div 
                          key={rating}
                          className="flex-1 text-center text-xs"
                          style={{
                            backgroundColor: rating <= 'C' ? '#dcfce7' : rating <= 'E' ? '#fef3c7' : '#fee2e2',
                            padding: '4px 2px',
                            borderRadius: '4px'
                          }}
                        >
                          {rating}: {analytics?.epc_ratings?.[rating] || 0}
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* EICR */}
              <Card>
                <CardHeader className="bg-blue-50">
                  <CardTitle className="flex items-center gap-2 text-blue-700">
                    <Shield className="w-5 h-5" />
                    EICR (Electrical)
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Valid</span>
                      <Badge variant="secondary">{analytics?.eicr_valid || 0}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Due in 30 days</span>
                      <Badge className="bg-amber-100 text-amber-700">{analytics?.eicr_due_soon || 0}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Overdue</span>
                      <Badge variant="destructive">{analytics?.eicr_overdue || 0}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Upcoming Expirations */}
              <Card className="md:col-span-3">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-amber-600" />
                    Upcoming Expirations (Next 30 Days)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-4">
                    {(analytics?.upcoming_expirations || []).map((item, idx) => (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-lg border ${
                          item.days_until <= 7 ? 'bg-red-50 border-red-200' : 
                          item.days_until <= 14 ? 'bg-amber-50 border-amber-200' : 
                          'bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <Badge variant="outline">{item.type}</Badge>
                          <span className="text-sm font-medium">
                            {item.days_until <= 0 ? 'OVERDUE' : `${item.days_until} days`}
                          </span>
                        </div>
                        <p className="font-medium text-sm">{item.property}</p>
                        <p className="text-xs text-muted-foreground">Expires: {item.expiry_date}</p>
                      </div>
                    ))}
                    {(!analytics?.upcoming_expirations || analytics.upcoming_expirations.length === 0) && (
                      <p className="col-span-3 text-center text-muted-foreground py-8">
                        No upcoming expirations in the next 30 days
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Properties Tab */}
          <TabsContent value="properties">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Occupancy */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Home className="w-5 h-5 text-blue-600" />
                    Occupancy Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <RechartsPie>
                      <Pie
                        data={[
                          { name: 'Occupied', value: propertyStats.occupied || 0 },
                          { name: 'Vacant', value: propertyStats.vacant || 0 }
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label
                      >
                        <Cell fill="#10b981" />
                        <Cell fill="#ef4444" />
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </RechartsPie>
                  </ResponsiveContainer>
                  <div className="text-center mt-4">
                    <p className="text-2xl font-bold">
                      {propertyStats.total_properties > 0 
                        ? ((propertyStats.occupied / propertyStats.total_properties) * 100).toFixed(1)
                        : 0}%
                    </p>
                    <p className="text-muted-foreground">Occupancy Rate</p>
                  </div>
                </CardContent>
              </Card>

              {/* Property by Area */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-purple-600" />
                    Properties by Postcode Area
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analytics?.properties_by_area || []} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="area" type="category" width={60} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#8b5cf6" name="Properties" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Management Fee Summary */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PoundSterling className="w-5 h-5 text-emerald-600" />
                    Management Fee Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold">£{(analytics?.total_monthly_fees || 0).toLocaleString()}</p>
                      <p className="text-sm text-muted-foreground">Monthly Fees</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold">{analytics?.avg_fee_percentage || 0}%</p>
                      <p className="text-sm text-muted-foreground">Avg Fee %</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold">£{(analytics?.total_annual_fees || 0).toLocaleString()}</p>
                      <p className="text-sm text-muted-foreground">Annual Fees</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-2xl font-bold">{analytics?.hmo_count || 0}</p>
                      <p className="text-sm text-muted-foreground">HMO Properties</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default LettingsAnalyticsPage;
